/**
 * Generates a stable device fingerprint from browser properties.
 * Uses screen resolution, timezone, language, platform, color depth,
 * and canvas fingerprint to create a SHA-256 hash.
 *
 * NOT meant to be a tracking fingerprint — it's a device identity
 * token that the user explicitly opts into via "Trust this device".
 */

async function sha256(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "no-canvas";

    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Prezntz FP", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Prezntz FP", 4, 17);

    return canvas.toDataURL();
  } catch {
    return "no-canvas";
  }
}

export async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    screen.width.toString(),
    screen.height.toString(),
    screen.colorDepth.toString(),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.platform,
    navigator.hardwareConcurrency?.toString() ?? "unknown",
    (navigator as any).deviceMemory?.toString() ?? "unknown",
    getCanvasFingerprint(),
  ];

  return sha256(components.join("|"));
}

/**
 * Derives a human-readable device name from the user agent string.
 * e.g. "Chrome on Windows", "Safari on iPhone", "Firefox on macOS"
 */
export function getDeviceName(): string {
  const ua = navigator.userAgent;

  let browser = "Unknown Browser";
  if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("OPR/") || ua.includes("Opera/")) browser = "Opera";
  else if (ua.includes("Chrome/") && !ua.includes("Edg/")) browser = "Chrome";
  else if (ua.includes("Safari/") && !ua.includes("Chrome/")) browser = "Safari";

  let os = "Unknown OS";
  if (ua.includes("iPhone") || ua.includes("iPad")) os = ua.includes("iPad") ? "iPad" : "iPhone";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("Mac OS X") || ua.includes("Macintosh")) os = "macOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("CrOS")) os = "ChromeOS";

  return `${browser} on ${os}`;
}
